import apiClient, { API_ENDPOINTS } from './config';

/* ---------- Type definitions ---------- */

export interface OrganizationItem
{
    id: number;
    name: string;
    organization_type: string | null;
    organization_size: string | null;
    created_by_user_id: number | null;
    created_at: string;
    updated_at: string | null;
    updated_by_user_id: number | null;
    feature_testcase_generation_enabled?: boolean;
    feature_test_execution_enabled?: boolean;
    feature_synthetic_data_enabled?: boolean;
    feature_byo_key_enabled?: boolean;
    feature_data_validation_agent_enabled?: boolean;
    feature_exploratory_agent_enabled?: boolean;
    feature_ritz_reservation_demo_enabled?: boolean;
    permissions?: {
        can_view_details: boolean;
        can_manage: boolean;
    };
}

export interface OrganizationDeletionPreview
{
    organization_id: number;
    organization_name: string;
    active_job_count: number;
    active_job_ids: number[];
    active_job_statuses: string[];
    active_member_count: number;
}

export interface ActiveOrganizationFeatureFlagsResponse
{
    organization_id: number | null;
    features: {
        testcase_generation: boolean;
        test_execution: boolean;
        synthetic_data: boolean;
        byo_key: boolean;
        data_validation_agent: boolean;
        exploratory_agent: boolean;
        ritz_reservation_demo: boolean;
    };
    maintenance?: Record<string, {
        maintenance_enabled: boolean;
        service_name: string;
        message: string;
    }>;
}

export interface OrganizationOverviewUser
{
    id: number;
    email: string;
    username?: string | null;
    full_name?: string | null;
    role?: string;
    is_active?: boolean;
    created_at?: string;
}

export interface OrganizationOverviewResponse extends OrganizationItem
{
    current_user_role?: string | null;
    summary?: {
        user_count?: number;
        active_user_count?: number;
        team_count?: number;
        project_count?: number;
        repository_count?: number;
        job_count?: number;
    };
    users?: {
        items?: OrganizationOverviewUser[];
        total?: number;
    };
    teams?: {
        items?: OrganizationTeam[];
        total?: number;
    };
    projects?: {
        items?: OrganizationProject[];
        total?: number;
    };
    jobs?: {
        items?: OrganizationJob[];
        total?: number;
    };
    llm_config?: {
        model?: string;
        temperature?: number;
    };
    prompts_count?: number;
    token_usage?: {
        prompt_tokens?: number;
        completion_tokens?: number;
        total_tokens?: number;
        job_count?: number;
        by_user?: Array<{
            user_id?: number | null;
            email?: string | null;
            prompt_tokens?: number;
            completion_tokens?: number;
            total_tokens?: number;
            job_count?: number;
        }>;
        by_model?: Array<{
            model?: string | null;
            prompt_tokens?: number;
            completion_tokens?: number;
            total_tokens?: number;
            job_count?: number;
        }>;
    };
    payment_info?: {
        configured?: boolean;
        current_period?: {
            total_tokens?: number;
            billed_tokens?: number;
            unbilled_tokens?: number;
        };
    };
}

export interface OrganizationUser
{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_admin: boolean;
    created_at: string;
}

export interface OrganizationTeam
{
    id: number;
    name: string;
    description?: string | null;
    organization_id?: number;
    created_at?: string;
    member_count?: number;
}

export interface OrganizationProject
{
    id: number;
    name: string;
    project_type?: string;
    organization_id?: number;
    team_id?: number | null;
    repository?: {
        id?: number;
        name?: string;
        status?: string;
    } | null;
    created_at?: string;
}

export interface OrganizationJob
{
    id: number;
    project_id?: number | null;
    project_name?: string | null;
    repository_id?: number | null;
    repository_name?: string | null;
    generation_kind?: string;
    job_type?: 'testcases' | 'playwright' | 'gherkin' | 'synthetic_data' | string;
    status: string;
    progress?: number;
    created_at?: string;
    completed_at?: string | null;
}

export interface OrganizationPromptsResponse
{
    prompts?: Array<{
        agent_name: string;
        content: string;
        description?: string;
        type?: string;
    }>;
}

export interface PagedOrganizationResponse
{
    items: OrganizationItem[];
    total: number;
    page: number;
    page_size: number;
}

export interface CreateOrganizationRequest
{
    name: string;
    organization_type?: string;
    organization_size?: string;
    assign_free_token_quota?: boolean;
}

export interface UpdateOrganizationRequest
{
    name?: string;
    organization_type?: string;
    organization_size?: string;
    feature_test_execution_enabled?: boolean;
    feature_synthetic_data_enabled?: boolean;
    feature_byo_key_enabled?: boolean;
    feature_data_validation_agent_enabled?: boolean;
    feature_exploratory_agent_enabled?: boolean;
    feature_ritz_reservation_demo_enabled?: boolean;
}

/* ---------- API functions ---------- */

export interface OrganizationFilterParams
{
    search?: string;
    organization_type?: string;
    date_from?: string;
    date_to?: string;
}

export interface FetchOrganizationsOptions
{
    ignoreActiveOrganizationScope?: boolean;
}

export async function fetchOrganizations(
    page: number,
    pageSize: number,
    includeDeleted = false,
    filters?: OrganizationFilterParams,
    options?: FetchOrganizationsOptions,
): Promise<PagedOrganizationResponse>
{
    const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        include_deleted: includeDeleted,
    };
    if (filters?.search) params.search = filters.search;
    if (filters?.organization_type) params.organization_type = filters.organization_type;
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;

    const requestConfig: Record<string, any> = { params };
    if (options?.ignoreActiveOrganizationScope)
    {
        requestConfig.headers = {
            'X-Active-Organization-Id': '0',
        };
    }

    const { data } = await apiClient.get<PagedOrganizationResponse>(API_ENDPOINTS.ORGANIZATIONS, requestConfig);
    return data;
}

export async function fetchMyOrganization(): Promise<OrganizationItem>
{
    const { data } = await apiClient.get<OrganizationItem>(API_ENDPOINTS.ORGANIZATION_ME);
    return data;
}

export async function fetchOrganization(id: number | string): Promise<OrganizationItem>
{
    const { data } = await apiClient.get<OrganizationItem>(API_ENDPOINTS.ORGANIZATION_DETAIL(id));
    return data;
}

export async function fetchOrganizationOverview(id: number | string): Promise<OrganizationOverviewResponse>
{
    const { data } = await apiClient.get<OrganizationOverviewResponse>(API_ENDPOINTS.ORGANIZATION_OVERVIEW(id));
    return data;
}

export async function createOrganization(payload: CreateOrganizationRequest): Promise<OrganizationItem>
{
    const { data } = await apiClient.post<OrganizationItem>(API_ENDPOINTS.ORGANIZATIONS, payload);
    return data;
}

export async function updateOrganization(
    id: number | string,
    payload: UpdateOrganizationRequest,
): Promise<OrganizationItem>
{
    const { data } = await apiClient.put<OrganizationItem>(API_ENDPOINTS.ORGANIZATION_DETAIL(id), payload);
    return data;
}

export async function deleteOrganization(id: number | string): Promise<void>
{
    await apiClient.delete(API_ENDPOINTS.ORGANIZATION_DETAIL(id));
}

export async function previewOrganizationDeletion(id: number | string): Promise<OrganizationDeletionPreview>
{
    const { data } = await apiClient.get<OrganizationDeletionPreview>(
        `${API_ENDPOINTS.ORGANIZATION_DETAIL(id)}/deletion-preview`,
    );
    return data;
}

export async function fetchOrganizationUsers(): Promise<OrganizationUser[]>
{
    const { data } = await apiClient.get<OrganizationUser[]>(API_ENDPOINTS.ORGANIZATION_USERS);
    return data;
}

export async function fetchOrganizationUsersByOrgId(id: number | string): Promise<OrganizationUser[]>
{
    const { data } = await apiClient.get<OrganizationUser[]>(API_ENDPOINTS.ORGANIZATION_USERS_BY_ID(id));
    return data || [];
}

export async function fetchOrganizationTeamsByOrgId(id: number | string): Promise<OrganizationTeam[]>
{
    const { data } = await apiClient.get<OrganizationTeam[]>(API_ENDPOINTS.ORGANIZATION_TEAMS_BY_ID(id));
    return data || [];
}

export async function fetchOrganizationProjects(id: number | string): Promise<OrganizationProject[]>
{
    const { data } = await apiClient.get<any>(API_ENDPOINTS.PROJECTS, {
        params: { organization_id: id },
    });
    return data?.items ?? (Array.isArray(data) ? data : []);
}

export async function fetchOrganizationJobs(): Promise<OrganizationJob[]>
{
    const { data } = await apiClient.get<{ items?: OrganizationJob[] }>(API_ENDPOINTS.TEST_GENERATIONS);
    return data?.items || [];
}

export async function fetchOrganizationLlmConfig(): Promise<{ model?: string; temperature?: number }>
{
    const { data } = await apiClient.get<{ model?: string; temperature?: number }>(API_ENDPOINTS.LLM_CONFIG);
    return data || {};
}

export async function fetchOrganizationPrompts(): Promise<OrganizationPromptsResponse>
{
    const { data } = await apiClient.get<OrganizationPromptsResponse>(API_ENDPOINTS.PROMPTS);
    return data || {};
}

export async function fetchActiveOrganizationFeatureFlags(): Promise<ActiveOrganizationFeatureFlagsResponse>
{
    const { data } = await apiClient.get<ActiveOrganizationFeatureFlagsResponse>(API_ENDPOINTS.ORGANIZATION_FEATURE_FLAGS_ACTIVE);
    return data;
}
